import { action, makeObservable, observable, runInAction } from 'mobx';

import {
  BookingService,
  ReviewIssue,
  ReviewIssueService,
  Sitter,
  SitterReview,
  SitterReviewService,
  SitterService,
} from 'us-web-services';

// Shared state for /sitter/:id/review (rating) and /sitter/:id/review/:reviewId
// (comments + issues). Angular used a singleton `SitterReviewV2.review`.
export class SitterReviewStore {
  review: SitterReview | null = null;
  sitter: Sitter | null = null;
  reasons: ReviewIssue[] = [];
  visibilityPublic: boolean = true;
  loading: boolean = false;

  constructor() {
    makeObservable(this, {
      review: observable,
      sitter: observable,
      reasons: observable,
      visibilityPublic: observable,
      loading: observable,
      loadForSitter: action,
      loadReasons: action,
      setRating: action,
      setComment: action,
      toggleVisibility: action,
      toggleReason: action,
      setIssuesSelection: action,
      save: action,
      clear: action,
    });
  }

  clear = (): void => {
    this.review = null;
    this.sitter = null;
    // Reasons are shared store state — clear selection flags so the next
    // review flow starts fresh instead of inheriting the prior sitter's picks.
    this.reasons = this.reasons.map((reason) => ({ ...reason, selected: false }));
    this.visibilityPublic = true;
  };

  setRating = (rating: number): void => {
    if (!this.review) return;

    // Angular: if previous rating was ≤3 and new > 3, clear comment
    if ((this.review.rating ?? 0) <= 3 && rating > 3) {
      this.review.comment = '';
    }
    this.review = { ...this.review, rating, ratingPercent: rating * 20 };
  };

  setComment = (comment: string): void => {
    if (!this.review) return;

    this.review = { ...this.review, comment };
  };

  toggleVisibility = (): void => {
    this.visibilityPublic = !this.visibilityPublic;
  };

  toggleReason = (type?: string): void => {
    if (!type) return;

    this.reasons = this.reasons.map((r) =>
      r.type === type ? { ...r, selected: !r.selected } : r,
    );
  };

  setIssuesSelection = (types: string[]): void => {
    const selected = new Set(types);
    this.reasons = this.reasons.map((r) => ({ ...r, selected: !!r.type && selected.has(r.type) }));
  };

  async loadReasons(): Promise<void> {
    if (this.reasons.length > 0) return;

    try {
      const reasons = (await ReviewIssueService.getByFilter()).data?.data ?? [];
      runInAction(() => {
        this.reasons = reasons;
      });
    } catch (err) {
      console.warn('[SitterReviewStore] loadReasons failed', err);
    }
  }

  // Loads existing review if one exists for this sitter+parent; otherwise seeds
  // a new in-memory review using the sitter/relationship. Returns `true` if
  // the parent is eligible to rate (has a completed/canceled booking), `false`
  // otherwise — callers use this to redirect to /recommend.
  async loadForSitter(sitterId: number, userId: number): Promise<boolean> {
    runInAction(() => { this.loading = true; });
    try {
      // If we already have an in-flight review in the store for this sitter, keep it.
      if (this.review && this.review.sitter?.id === sitterId) {
        return true;
      }

      const existingReview = (await SitterReviewService.getByFilter({
        params: { parentId: userId, userId, sitterId },
      })).data?.data?.[0] ?? null;

      if (existingReview) {
        runInAction(() => {
          this.review = { ...existingReview, relationship: existingReview.relationship ?? 'hired' };
          this.sitter = existingReview.sitter ?? null;
          this.visibilityPublic = existingReview.visibility !== 'private';
        });
      } else {
        const sitter = (await SitterService.get(sitterId, {
          params: { parentId: userId, userId },
        })).data?.data ?? null;
        const hasBooked = (sitter?.relationship?.bookingCount ?? 0) > 0;

        runInAction(() => {
          this.sitter = sitter;
          this.review = {
            comment: '',
            rating: 0,
            relationship: hasBooked ? 'hired' : 'interviewed',
            visibility: 'public',
            sitter: sitter ?? undefined,
            parent: { id: userId },
          };
          this.visibilityPublic = true;
        });
      }

      // Check eligibility: must have ≥1 completed/canceled booking with this sitter.
      const bookingMeta = (await BookingService.getByFilter({
        params: {
          limit: 1,
          sitterId,
          parentId: userId,
          userId,
          statuses: ['closed_completed', 'parent_cancel', 'sitter_cancel'],
        },
      })).data?.meta;

      return (bookingMeta?.size ?? 0) > 0;
    } finally {
      runInAction(() => { this.loading = false; });
    }
  }

  async save(): Promise<SitterReview | null> {
    if (!this.review) return null;

    const issues = this.reasons.filter((r) => r.selected).map((r) => ({ type: r.type, label: r.label }));
    const body: SitterReview = {
      ...this.review,
      visibility: this.visibilityPublic ? 'public' : 'private',
      issues,
    };
    const responseData = (body.id
      ? await SitterReviewService.patch(body.id, body)
      : await SitterReviewService.create(body)
    ).data?.data;
    const saved = (responseData ?? body) as SitterReview;
    runInAction(() => { this.review = saved; });

    return saved;
  }
}
